import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { ProductImageSlider } from './ProductImageSlider';
import type { Product, ProductVariant } from '../types/product';

interface ProductCardProps {
  product: Product;
  onAddToCart: (variant: ProductVariant) => void;
}

export function ProductCard({ product, onAddToCart }: ProductCardProps) {
  if (!product.variants || product.variants.length === 0) {
    return null;
  }
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const selectedVariant = product.variants[selectedVariantIndex];
  
  if (!selectedVariant || !selectedVariant.images || selectedVariant.images.length === 0) {
    return null;
  }

  // Get the product name from the variant
  const productName = selectedVariant.name || 'Product Name';

  const handleVariantChange = (index: number) => {
    const newVariant = product.variants[index];
    
    // If the new variant has fewer images than the current image index,
    // reset to the first image, otherwise keep the current position
    if (newVariant.images && currentImageIndex >= newVariant.images.length) {
      setCurrentImageIndex(0);
    }
    
    setSelectedVariantIndex(index);
  };

  const getStockStatus = (stock: number) => {
    if (stock > 3) {
      return <span className="text-green-500">Tellitav</span>;
    } else if (stock === 2 || stock === 3) {
      return <span className="text-orange-400">Vaid {stock} tellitavad!</span>;
    } else if (stock === 1) {
      return <span className="text-orange-500">Viimane tellitav!</span>;
    } else {
      return <span className="text-red-500">Laost otsas</span>;
    }
  };

  const discountBadge = selectedVariant.discounted_price ? {
    text: `-${Math.round(((selectedVariant.price - selectedVariant.discounted_price) / selectedVariant.price) * 100)}%`
  } : null;
  
  // Check for new badge - support both new and isNew properties
  const isNew = product.isNew || product.new || 
                selectedVariant.isNew || selectedVariant.new;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-xl transition-all duration-300">
      <Link to={`/tooted/${selectedVariant.id}`}>
        <ProductImageSlider 
          images={selectedVariant.images}
          name={productName}
          brand={product.brand}
          productClass={product.class}
          tags={product.tags}
          badge={discountBadge}
          newBadge={isNew}
          currentImageIndex={currentImageIndex}
          onImageIndexChange={setCurrentImageIndex}
        />
      </Link>
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">{productName}</h3>
        
        <div className="flex gap-2 mb-4">
          {product.variants.map((variant, index) => (
            <button
              key={variant.id}
              onClick={() => handleVariantChange(index)}
              className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full border-2 transition-all ${
                selectedVariantIndex === index 
                  ? 'border-[#4FC1E9] scale-110' 
                  : 'border-transparent hover:border-gray-200'
              }`}
              style={{ backgroundColor: variant.color_code }}
              title={variant.color}
            />
          ))}
        </div>

        <div className="flex flex-col space-y-2">
          <div>
            {selectedVariant.discounted_price ? (
              <>
                <span className="text-[#4FC1E9] font-bold text-xl">
                  {selectedVariant.discounted_price.toFixed(2)}€
                </span>
                <span className="text-gray-400 line-through text-sm ml-2">
                  {selectedVariant.price.toFixed(2)}€
                </span>
              </>
            ) : (
              <span className="text-[#4FC1E9] font-bold text-xl">
                {selectedVariant.price.toFixed(2)}€
              </span>
            )}
          </div>
          <div className="flex justify-between items-center">
            {getStockStatus(selectedVariant.stock)}
            <button 
              onClick={(e) => {
                e.preventDefault();
                onAddToCart({
                  ...selectedVariant,
                  stock: selectedVariant.stock // Pass stock to enforce limits
                });
              }}
              disabled={selectedVariant.stock <= 0}
              className={`${
                selectedVariant.stock > 0 
                ? "bg-[#4FC1E9] hover:bg-[#3ba8d0]"
                : "bg-gray-300 cursor-not-allowed"
              } text-white p-2 rounded-full transition-colors duration-300`}
              aria-label={selectedVariant.stock > 0 ? `Lisa ${selectedVariant.name} ostukorvi` : "Toode pole saadaval"}
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}